"use client";;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:ENCON_SENTRY_KEY":true});}catch(e){}}();

import type {StaticImageData} from "next/image";
import NextImage from "next/image";
import type React from "react";
import cssVariables from "@/app/cssVariables";
import type { Media as MediaType } from "@/payload-types";
import {getClientSideURL} from "@/utilities/getURL";
import {cn} from "@/utilities/ui";
import type {Props as MediaProps} from "../types";

const { breakpoints } = cssVariables;

// Largest-first; only width-only variants (preserve aspect ratio).
// Payload bakes crops into the main file and regenerates these sizes from it.
// An uncropped original is kept separately (originalBackupKey) for restore.
const CROPPED_SIZE_PRIORITY = [
	"xlarge",
	"large",
	"medium",
	"small",
	"thumbnail",
] as const;

const pickCroppedSource = (
	resource: MediaType,
): { url?: string | null; width?: number | null; height?: number | null } => {
	for (const key of CROPPED_SIZE_PRIORITY) {
		const variant = resource.sizes?.[key];
		if (variant?.url) return variant;
	}
	return { url: resource.url, width: resource.width, height: resource.height };
};

// Responsive breakpoints mapping PayloadCMS sizes to media queries.
// The browser picks the first matching <source> for the viewport width,
// so images are served directly from R2 at the appropriate resolution.
const RESPONSIVE_SOURCES = [
	{ key: "small" as const, maxWidth: 640 },
	{ key: "medium" as const, maxWidth: 1024 },
	{ key: "large" as const, maxWidth: 1440 },
];


export const ImageMedia: React.FC<MediaProps> = (props) => {
	const {
		alt: altFromProps,
		fill,
		imgClassName,
		priority,
		resource,
		size: sizeFromProps,
		src: srcFromProps,
		loading: loadingFromProps,
	} = props;

	let width: number | undefined;
	let height: number | undefined;
	let alt = altFromProps;
	let src: StaticImageData | string = srcFromProps || "";

	let cacheTag: string | undefined;

	if (!src && resource && typeof resource === "object") {
		const cropped = pickCroppedSource(resource);
		const url = cropped.url;

		width = cropped.width ?? undefined;
		height = cropped.height ?? undefined;
		alt = resource.alt || "";

		cacheTag = resource.updatedAt;

		src = url?.startsWith("http")
			? `${url}?${cacheTag}`
			: `${getClientSideURL()}${url}?${cacheTag}`;
	}

	const loading = loadingFromProps || (!priority ? "lazy" : undefined);

	// NOTE: this is used by the browser to determine which image to download at different screen sizes
	const sizes = sizeFromProps
		? sizeFromProps
		: Object.entries(breakpoints)
				.map(([, value]) => `(max-width: ${value}px) ${value * 2}w`)
				.join(", ");

	const isExternal = typeof src === "string" && src.startsWith("http");

	// Build responsive <source> elements from PayloadCMS size variants
	// so the browser picks the right resolution directly from R2
	const responsiveSources =
		isExternal && resource && typeof resource === "object"
			? RESPONSIVE_SOURCES.map(({ key, maxWidth }) => {
					const variant = resource.sizes?.[key];
					if (!variant?.url) return null;
					const variantSrc = variant.url.startsWith("http")
						? `${variant.url}?${cacheTag}`
						: `${getClientSideURL()}${variant.url}?${cacheTag}`;
					return (
						<source
							key={key}
							media={`(max-width: ${maxWidth}px)`}
							srcSet={variantSrc}
						/>
					);
				}).filter(Boolean)
			: null;

	// Apply focal point as object-position. Only visible when CSS sets
	// object-fit (cover/contain); inert otherwise, so safe to always emit.
	const focalStyle =
		resource && typeof resource === "object" &&
		resource.focalX != null && resource.focalY != null
			? { objectPosition: `${resource.focalX}% ${resource.focalY}%` }
			: undefined;

	return (
		<picture>
			{responsiveSources}
			<NextImage
				alt={alt || ""}
				className={cn(imgClassName)}
				fill={fill}
				height={!fill ? height : undefined}
				priority={priority}
				quality={75}
				loading={loading}
				sizes={sizes}
				src={src}
				style={focalStyle}
				width={!fill ? width : undefined}
				unoptimized={isExternal}
			/>
		</picture>
	);
};
